import { useCallback, useEffect, useRef, useState } from 'react';
import {
  drawFormation,
  hitTest,
  tokenRadius,
  toCanvasPoint,
  type RenderPlacement,
} from '@/diagrams/formationCanvas';

/**
 * The interactive pitch. Players are dragged to where they should stand.
 *
 * Pointer events rather than mouse events, so it works with a finger on the
 * touchline as well as a mouse at a desk. Positions are stored as fractions of
 * the pitch, so a shape arranged on a phone prints identically on A4.
 */
export function FormationBoard({
  placements,
  selectedIndex,
  onSelect,
  onMove,
  readOnly = false,
}: {
  placements: RenderPlacement[];
  selectedIndex: number | null;
  onSelect: (index: number | null) => void;
  onMove: (index: number, x: number, y: number) => void;
  readOnly?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 900, h: 600 });
  const dragging = useRef<number | null>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const observer = new ResizeObserver(([entry]) => {
      const w = Math.max(280, Math.floor(entry?.contentRect.width ?? 720));
      // A pitch is 105x68, drawn upright: taller than it is wide, at the real
      // ratio so a shape is never read through a distorted picture.
      setSize({ w, h: Math.round(w * 1.45) });
    });
    observer.observe(wrap);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size.w * dpr;
    canvas.height = size.h * dpr;
    canvas.style.width = `${size.w}px`;
    canvas.style.height = `${size.h}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawFormation(ctx, size.w, size.h, placements, {
      selectedIndex: selectedIndex ?? undefined,
    });
  }, [placements, selectedIndex, size]);

  const pointFrom = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      return { px: e.clientX - rect.left, py: e.clientY - rect.top };
    },
    [],
  );

  /** Inverse of toCanvasPoint, clamped so a player cannot be dragged off. */
  const toFraction = useCallback(
    (px: number, py: number) => {
      const inset = 0.07;
      const usableW = size.w * (1 - inset * 2);
      const usableH = size.h * (1 - inset * 2);
      const x = (px - size.w * inset) / usableW;
      const y = 1 - (py - size.h * inset) / usableH;
      return {
        x: Math.min(1, Math.max(0, x)),
        y: Math.min(1, Math.max(0, y)),
      };
    },
    [size],
  );

  return (
    <div ref={wrapRef} className="w-full">
      <canvas
        ref={canvasRef}
        className={`block w-full rounded-(--radius-panel) ${
          readOnly ? '' : 'cursor-grab touch-none active:cursor-grabbing'
        }`}
        onPointerDown={(e) => {
          if (readOnly) return;
          const { px, py } = pointFrom(e);
          const index = hitTest(placements, px, py, size.w, size.h, tokenRadius(size.w) * 1.2);
          onSelect(index >= 0 ? index : null);
          if (index >= 0) {
            dragging.current = index;
            e.currentTarget.setPointerCapture(e.pointerId);
          }
        }}
        onPointerMove={(e) => {
          if (readOnly || dragging.current === null) return;
          const { px, py } = pointFrom(e);
          const { x, y } = toFraction(px, py);
          onMove(dragging.current, x, y);
        }}
        onPointerUp={(e) => {
          dragging.current = null;
          e.currentTarget.releasePointerCapture(e.pointerId);
        }}
        onPointerCancel={() => {
          dragging.current = null;
        }}
      />
      {/* Keyboard equivalent: a pitch that can only be used with a pointer is
          not usable by everyone, and nudging is often more precise anyway. */}
      {!readOnly && selectedIndex !== null && placements[selectedIndex] ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-meta text-ink-muted">
          <span>{placements[selectedIndex]?.name ?? placements[selectedIndex]?.positionKey}</span>
          {(
            [
              ['←', -0.02, 0],
              ['→', 0.02, 0],
              ['↑', 0, 0.02],
              ['↓', 0, -0.02],
            ] as const
          ).map(([label, dx, dy]) => (
            <button
              key={label}
              type="button"
              aria-label={label}
              onClick={() => {
                const p = placements[selectedIndex]!;
                onMove(
                  selectedIndex,
                  Math.min(1, Math.max(0, p.x + dx)),
                  Math.min(1, Math.max(0, p.y + dy)),
                );
              }}
              className="size-8 rounded-(--radius-control) border border-line bg-surface hover:border-ink"
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export { toCanvasPoint };
